// 承認待ちパフォーマー一覧(設計書 §3.5.3)
// 承認 / 却下を 1 アクションで実行できる。

import { ApiClientError } from '../../api/client';
import {
  useApprovePerformer,
  usePendingPerformers,
  useRejectPerformer,
  type PendingPerformer,
} from '../../api/admin/performers';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';

export function AdminPendingPerformersPage() {
  const pendingQuery = usePendingPerformers();
  const approveMutation = useApprovePerformer();
  const rejectMutation = useRejectPerformer();

  return (
    <AdminLayout title="承認待ち" description="新規申請のあったパフォーマーを承認または却下します。">
      {pendingQuery.isLoading && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>読み込み中…</p>
        </Card>
      )}

      {pendingQuery.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {pendingQuery.error instanceof ApiClientError
              ? pendingQuery.error.message
              : '取得に失敗しました'}
          </p>
        </Card>
      )}

      {pendingQuery.data && pendingQuery.data.length === 0 && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            承認待ちのパフォーマーはいません。
          </p>
        </Card>
      )}

      {pendingQuery.data?.map((p) => (
        <PendingRow
          key={p.ulid}
          performer={p}
          onApprove={() => approveMutation.mutate(p.ulid)}
          onReject={() => rejectMutation.mutate(p.ulid)}
          approving={
            approveMutation.isPending && approveMutation.variables === p.ulid
          }
          rejecting={
            rejectMutation.isPending && rejectMutation.variables === p.ulid
          }
        />
      ))}

      {(approveMutation.error || rejectMutation.error) && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {(approveMutation.error ?? rejectMutation.error) instanceof ApiClientError
              ? (approveMutation.error ?? rejectMutation.error)!.message
              : '処理に失敗しました'}
          </p>
        </Card>
      )}
    </AdminLayout>
  );
}

interface PendingRowProps {
  performer: PendingPerformer;
  onApprove: () => void;
  onReject: () => void;
  approving: boolean;
  rejecting: boolean;
}

function PendingRow({ performer, onApprove, onReject, approving, rejecting }: PendingRowProps) {
  return (
    <Card>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 16,
          alignItems: 'center',
        }}
      >
        <div>
          <h3 style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 500 }}>
            {performer.display_name}
          </h3>
          <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
            {performer.email}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--color-text-tertiary)', marginTop: 2 }}>
            申請日時: {new Date(performer.applied_at).toLocaleString('ja-JP')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ minWidth: 90 }}>
            <Button
              onClick={onApprove}
              disabled={approving || rejecting}
              style={{ padding: '6px 10px', fontSize: '11px' }}
            >
              {approving ? '承認中…' : '承認'}
            </Button>
          </div>
          <div style={{ minWidth: 90 }}>
            <Button
              variant="danger"
              onClick={onReject}
              disabled={approving || rejecting}
              style={{ padding: '6px 10px', fontSize: '11px' }}
            >
              {rejecting ? '却下中…' : '却下'}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
